import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import LinkIcon from '@atlaskit/icon/core/link';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { SectionCountBadge } from '../SectionCountBadge.tsx';

import { AppearsInSection } from './AppearsInSection.tsx';
import { LinksToSection } from './LinksToSection.tsx';
import { SectionEmptyState } from './SectionEmptyState.tsx';
import { SyncedFromSection } from './SyncedFromSection.tsx';
import type { AppearsInData, LinksToData, SyncedFromData } from './useConnectionSectionsData.ts';
import { getSectionExpansionProps } from './useSectionExpansion.ts';
import type { SectionExpansionController } from './useSectionExpansion.ts';

const styles = cssMap({
	card: {
		display: 'flex',
		flexDirection: 'column',
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderRadius: token('radius.large'),
		overflow: 'hidden',
		backgroundColor: token('elevation.surface'),
		boxSizing: 'border-box',
	},
	linksGroup: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.150'),
	},
	linksHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
});

const i18n = defineMessages({
	links: {
		id: 'byline-connections.preview-panel.links-header',
		defaultMessage: 'Links',
		description:
			'Header at the top of the Teamwork Graph connections card, above the individual connection sections. The total link count is shown in a badge beside it.',
	},
	linksEmpty: {
		id: 'byline-connections.preview-panel.links-empty',
		defaultMessage: 'This content isn’t linked with anything yet.',
		description:
			'Shown under the "Links" header of the Teamwork Graph connections panel, in place of the connection sections, when the page has no links to, from, or related to it.',
	},
});

export type LinksGroupProps = {
	syncedFrom: SyncedFromData;
	appearsIn: AppearsInData;
	linksTo: LinksToData;
	onPreviewOpen?: () => void;
	onAskRovo?: (prompt?: string) => void;
	sectionExpansion?: SectionExpansionController;
};

/**
 * Related content has a separate card, so it must not affect Links totals or the empty state.
 */
export const LinksGroup = ({
	syncedFrom,
	appearsIn,
	linksTo,
	onPreviewOpen,
	onAskRovo,
	sectionExpansion,
}: LinksGroupProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const totalLinks =
		(syncedFrom.connection ? 1 : 0) + appearsIn.links.length + linksTo.links.length;
	// Links to and Synced from are synchronous; only Appears in can still fill the card.
	// A failed incoming query is not an empty graph, so keep its card while loading or errored.
	const hasNoLinks = totalLinks === 0 && !appearsIn.loading && !appearsIn.error;

	return (
		<Box xcss={styles.linksGroup}>
			<Box xcss={styles.linksHeader}>
				<LinkIcon label="" color="currentColor" />
				<Text weight="semibold" color="color.text">
					{formatMessage(i18n.links)}
				</Text>
				{totalLinks ? (
					<SectionCountBadge count={totalLinks} hasMore={appearsIn.hasNextPage} />
				) : null}
			</Box>
			{hasNoLinks ? (
				<SectionEmptyState
					message={formatMessage(i18n.linksEmpty)}
					testId="teamwork-graph-preview-panel-links-empty"
				/>
			) : (
				<Box xcss={styles.card}>
					<SyncedFromSection
						{...syncedFrom}
						{...getSectionExpansionProps(sectionExpansion, 'syncedFrom')}
					/>
					<AppearsInSection
						{...appearsIn}
						onPreviewOpen={onPreviewOpen}
						onAskRovo={onAskRovo}
						{...getSectionExpansionProps(sectionExpansion, 'appearsIn')}
					/>
					<LinksToSection
						{...linksTo}
						onPreviewOpen={onPreviewOpen}
						onAskRovo={onAskRovo}
						{...getSectionExpansionProps(sectionExpansion, 'linksTo')}
					/>
				</Box>
			)}
		</Box>
	);
};
